import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, AlertTriangle, Award } from 'lucide-react';
import { useFeatureAccess } from '../../../hooks/useFeatureAccess';
import apiClient from '../../../api/client';
import UpgradePrompt from '../../subscription/UpgradePrompt';
import './CustomerInsightsList.css';

// Plain-language helpers
function getCustomerTypeLabel(segment) {
  switch (segment) {
    case 'vip':       return { label: '🏆 VIP',           color: 'type-vip',      desc: 'High spender, buys regularly' };
    case 'loyal':     return { label: '🟢 Regular buyer', color: 'type-loyal',    desc: 'Consistent, reliable customer' };
    case 'potential': return { label: '🔵 Growing',       color: 'type-potential',desc: 'Still building a habit' };
    case 'at_risk':   return { label: '🟡 At risk',       color: 'type-at-risk',  desc: 'Used to buy, now slowing down' };
    case 'inactive':  return { label: '🔴 Lost',          color: 'type-inactive', desc: 'Hasn\'t bought in 6+ months' };
    default:          return { label: '—',                color: '',              desc: '' };
  }
}

function getLastBoughtLabel(days) {
  if (days === 0)  return 'Today';
  if (days <= 7)   return `${days} days ago`;
  if (days <= 30)  return `${days} days ago`;
  if (days <= 60)  return `${days} days ago ⚠️`;
  return                  `${days} days ago 🔴`;
}

function getActionLabel(segment, discount, actions) {
  switch (segment) {
    case 'vip':       return `Give ${discount}% discount — treat them as priority`;
    case 'loyal':     return `Offer ${discount}% discount — reward loyalty`;
    case 'potential': return `Send a ${discount}% welcome offer`;
    case 'at_risk':   return `Send ${discount}% win-back offer — act soon`;
    case 'inactive':  return `Try ${discount}% offer — they may have forgotten you`;
    default:          return actions || '—';
  }
}

function CustomerInsightsList() {
  const navigate = useNavigate();
  const { hasFeature } = useFeatureAccess();
  const [insights, setInsights]             = useState([]);
  const [loading, setLoading]               = useState(true);
  const [filter, setFilter]                 = useState('all');
  const [sortBy, setSortBy]                 = useState('spent');
  // ⭐ FIX: Add state to control the modal
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);

  useEffect(() => { loadInsights(); }, []);

  async function loadInsights() {
    try {
      const response = await apiClient.get('/customer-insights/');
      const data = Array.isArray(response.data)
        ? response.data
        : (response.data.results || []);
      setInsights(data);
    } catch (error) {
      console.error('Failed to load customer insights:', error);
    }
    setLoading(false);
  }

  function getFilteredInsights() {
    let filtered = [...insights];
    if (filter !== 'all') {
      filtered = filtered.filter(i => i.segment === filter);
    }
    filtered.sort((a, b) => {
      if (sortBy === 'spent')  return parseFloat(b.total_spent) - parseFloat(a.total_spent);
      if (sortBy === 'orders') return b.total_orders - a.total_orders;
      if (sortBy === 'days')   return a.days_since_last_order - b.days_since_last_order;
      if (sortBy === 'churn')  return parseFloat(b.churn_risk) - parseFloat(a.churn_risk);
      return 0;
    });
    return filtered;
  }

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Loading customer insights...</p>
      </div>
    );
  }

  // ⭐ FIX: Render locked screen with a button that opens the modal,
  //         and pass onClose so the X button works
  if (!hasFeature('customer_segments')) {
    return (
      <>
        <div className="locked-screen">
          <Users size={48} />
          <h2>Customer Insights</h2>
          <p>Upgrade to Business plan for customer analysis</p>
          <button
            onClick={() => setShowUpgradePrompt(true)}
            className="btn-primary"
          >
            Upgrade Now
          </button>
        </div>

        {showUpgradePrompt && (
          <UpgradePrompt
            feature="customer_segments"
            onClose={() => setShowUpgradePrompt(false)}
          />
        )}
      </>
    );
  }

  if (insights.length === 0) {
    return (
      <div className="customer-insights-list fade-in">
        <div className="insights-header">
          <div>
            <h1>Your Customers</h1>
            <p className="subtitle">Who is buying from you — and what to do about each one</p>
          </div>
          <button onClick={() => navigate('/analytics')} className="btn-secondary">
            ← Analytics full review
          </button>
        </div>
        <div className="empty-state">
          <Users size={48} />
          <p className="empty-title">No data yet</p>
          <p className="empty-desc">
            Go to <strong>Analytics</strong> and click <strong>Run Analytics</strong> to analyse your customers.
          </p>
          <button onClick={() => navigate('/analytics')} className="btn-primary">Go to Analytics</button>
        </div>
      </div>
    );
  }

  const counts = {
    vip:       insights.filter(i => i.segment === 'vip').length,
    loyal:     insights.filter(i => i.segment === 'loyal').length,
    potential: insights.filter(i => i.segment === 'potential').length,
    at_risk:   insights.filter(i => i.segment === 'at_risk').length,
    inactive:  insights.filter(i => i.segment === 'inactive').length,
  };

  const filteredInsights = getFilteredInsights();
  const lastCalculated   = insights[0]?.last_calculated
    ? new Date(insights[0].last_calculated).toLocaleString()
    : null;

  return (
    <div className="customer-insights-list fade-in">
      <div className="insights-header">
        <div>
          <h1>Your Customers</h1>
          <p className="subtitle">Who is buying from you — and what to do about each one</p>
          {lastCalculated && (
            <p className="last-calculated">Last updated: {lastCalculated}</p>
          )}
        </div>
        <button onClick={() => navigate('/analytics')} className="btn-secondary">
          ← Analytics full review
        </button>
      </div>

      {/* Summary cards */}
      <div className="segment-summary">
        <div className="summary-card vip">
          <Award size={20} />
          <div>
            <span className="count">{counts.vip}</span>
            <span className="label">VIP customers</span>
          </div>
        </div>
        <div className="summary-card loyal">
          <Users size={20} />
          <div>
            <span className="count">{counts.loyal}</span>
            <span className="label">Regular buyers</span>
          </div>
        </div>
        <div className="summary-card potential">
          <Users size={20} />
          <div>
            <span className="count">{counts.potential}</span>
            <span className="label">Growing</span>
          </div>
        </div>
        <div className="summary-card at-risk">
          <AlertTriangle size={20} />
          <div>
            <span className="count">{counts.at_risk}</span>
            <span className="label">At risk of leaving</span>
          </div>
        </div>
        <div className="summary-card inactive">
          <Users size={20} />
          <div>
            <span className="count">{counts.inactive}</span>
            <span className="label">Lost customers</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="insights-controls">
        <div className="filter-tabs">
          <button className={filter === 'all'       ? 'tab active' : 'tab'} onClick={() => setFilter('all')}>All ({insights.length})</button>
          <button className={filter === 'vip'       ? 'tab active' : 'tab'} onClick={() => setFilter('vip')}>🏆 VIP ({counts.vip})</button>
          <button className={filter === 'loyal'     ? 'tab active' : 'tab'} onClick={() => setFilter('loyal')}>🟢 Regular ({counts.loyal})</button>
          <button className={filter === 'potential' ? 'tab active' : 'tab'} onClick={() => setFilter('potential')}>🔵 Growing ({counts.potential})</button>
          <button className={filter === 'at_risk'   ? 'tab active' : 'tab'} onClick={() => setFilter('at_risk')}>🟡 At Risk ({counts.at_risk})</button>
          <button className={filter === 'inactive'  ? 'tab active' : 'tab'} onClick={() => setFilter('inactive')}>🔴 Lost ({counts.inactive})</button>
        </div>

        <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="sort-select">
          <option value="spent">Sort by total spent</option>
          <option value="orders">Sort by number of orders</option>
          <option value="days">Sort by most recent</option>
          {hasFeature('churn_prediction') && <option value="churn">Sort by risk of leaving</option>}
        </select>
      </div>

      {/* Table */}
      <div className="insights-table-container">
        <table className="insights-table customer-table">
          <thead>
            <tr>
              <th>Customer</th>
              <th>Type</th>
              <th>Total spent</th>
              <th>Orders</th>
              <th>Last bought</th>
              {hasFeature('churn_prediction') && <th>Risk of leaving</th>}
              {hasFeature('rfm_analysis') && <th>What to do</th>}
            </tr>
          </thead>
          <tbody>
            {filteredInsights.map(insight => {
              const type   = getCustomerTypeLabel(insight.segment);
              const action = getActionLabel(
                insight.segment,
                parseFloat(insight.recommended_discount).toFixed(0),
                insight.recommended_actions
              );
              const churnRisk = parseFloat(insight.churn_risk);

              return (
                <tr key={insight.id} className={`segment-row segment-${insight.segment}`}>
                  <td className="customer-info">
                    <div className="customer-name">{insight.customer_name}</div>
                    <div className="customer-contact">
                      {insight.customer_email || insight.customer_phone || '—'}
                    </div>
                  </td>
                  <td>
                    <div className={`type-badge ${type.color}`}>
                      {type.label}
                    </div>
                    <div className="type-desc">{type.desc}</div>
                  </td>
                  <td className="spent-cell">
                    <div className="spent-main">
                      TSh {parseFloat(insight.total_spent).toLocaleString()}
                    </div>
                    <div className="spent-sub">
                      {insight.total_orders} order{insight.total_orders !== 1 ? 's' : ''} · avg TSh {parseFloat(insight.avg_order_value).toLocaleString()} each
                    </div>
                  </td>
                  <td className="orders-cell">{insight.total_orders}</td>
                  <td className="last-order-cell">
                    {getLastBoughtLabel(insight.days_since_last_order)}
                  </td>
                  {hasFeature('churn_prediction') && (
                    <td>
                      {churnRisk >= 70
                        ? <span className="churn-badge churn-high">🔴 High — act now</span>
                        : churnRisk >= 40
                        ? <span className="churn-badge churn-medium">🟡 Medium</span>
                        : <span className="churn-badge churn-low">🟢 Low</span>
                      }
                    </td>
                  )}
                  {hasFeature('rfm_analysis') && (
                    <td className="action-cell">
                      {action}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {filteredInsights.length === 0 && (
        <div className="empty-state">
          <Users size={48} />
          <p>No customers in this category</p>
        </div>
      )}
    </div>
  );
}

export default CustomerInsightsList;